/**
 * Create Organization Dialog (Superadmin)
 *
 * Creates orgs without adding superadmin as member.
 */

import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToast,
} from "@wondermaps/ui";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/pro-solid-svg-icons/faPlus";
import { faSpinner } from "@fortawesome/pro-solid-svg-icons/faSpinner";
import { api } from "../../lib/api";

import { ORG_TYPES, ORG_TIERS } from "@wondermaps/db/schema";

type OrgType = (typeof ORG_TYPES)[number];
type OrgTier = (typeof ORG_TIERS)[number];

interface FormData {
  name: string;
  slug: string;
  type: OrgType;
  tier: OrgTier;
}

const defaultFormData: FormData = {
  name: "",
  slug: "",
  type: "default",
  tier: "base",
};

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateOrgDialog({ open, onOpenChange }: Props) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [formData, setFormData] = useState<FormData>(defaultFormData);
  const [slugError, setSlugError] = useState<string | null>(null);
  const [autoSlug, setAutoSlug] = useState(true);

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setFormData(defaultFormData);
      setSlugError(null);
      setAutoSlug(true);
    }
  }, [open]);

  // Auto-generate slug from name
  useEffect(() => {
    if (autoSlug && formData.name) {
      setFormData((prev) => ({ ...prev, slug: slugify(prev.name) }));
      setSlugError(null);
    }
  }, [formData.name, autoSlug]);

  const createOrg = useMutation({
    mutationFn: async (data: FormData) => {
      const res = await api.orgs.admin.$post({
        json: { name: data.name, slug: data.slug, type: data.type, tier: data.tier },
      });
      if (!res.ok) {
        const err = (await res.json()) as { error: string; field?: string };
        if (res.status === 409 && err.field === "slug") {
          throw { type: "slug_conflict", message: err.error };
        }
        throw new Error(err.error || "Failed to create organization");
      }
      return res.json() as Promise<{ org: { id: string; name: string; slug: string } }>;
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ["superadmin", "orgs"] });
      toast({ title: `Created "${data.org.name}"` });
      onOpenChange(false);
      navigate({ to: "/superadmin/orgs/$orgId", params: { orgId: data.org.id } });
    },
    onError: (err: unknown) => {
      if (typeof err === "object" && err !== null && "type" in err) {
        const e = err as { type: string; message: string };
        if (e.type === "slug_conflict") {
          setSlugError(e.message);
          return;
        }
      }
      toast({
        title: "Error",
        description: err instanceof Error ? err.message : "Failed to create organization",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSlugError(null);
    createOrg.mutate(formData);
  };

  const handleSlugChange = (value: string) => {
    setAutoSlug(false);
    setSlugError(null);
    setFormData((prev) => ({ ...prev, slug: slugify(value) }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FontAwesomeIcon icon={faPlus} className="h-5 w-5" />
            Create Organization
          </DialogTitle>
          <DialogDescription>
            Create a new organization. Use InviteGenerator to assign an owner afterward.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="Acme Zoo"
              disabled={createOrg.isPending}
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="slug">Slug</Label>
            <Input
              id="slug"
              value={formData.slug}
              onChange={(e) => handleSlugChange(e.target.value)}
              placeholder="acme-zoo"
              disabled={createOrg.isPending}
              className={slugError ? "border-destructive" : ""}
            />
            {slugError && <p className="text-sm text-destructive">{slugError}</p>}
            <p className="text-xs text-muted-foreground">
              Used in URLs: wondermaps.co/{formData.slug || "slug"}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="type">Type</Label>
            <Select
              value={formData.type}
              onValueChange={(v) => setFormData((prev) => ({ ...prev, type: v as OrgType }))}
              disabled={createOrg.isPending}
            >
              <SelectTrigger id="type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ORG_TYPES.map((type) => (
                  <SelectItem key={type} value={type} className="capitalize">
                    {type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="tier">Tier</Label>
            <Select
              value={formData.tier}
              onValueChange={(v) => setFormData((prev) => ({ ...prev, tier: v as OrgTier }))}
              disabled={createOrg.isPending}
            >
              <SelectTrigger id="tier">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ORG_TIERS.map((tier) => (
                  <SelectItem key={tier} value={tier} className="capitalize">
                    {tier}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={createOrg.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!formData.name.trim() || !formData.slug.trim() || createOrg.isPending}
            >
              {createOrg.isPending && <FontAwesomeIcon icon={faSpinner} spin className="mr-2 h-4 w-4 " />}
              {createOrg.isPending ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
